import type { Variation } from '@gonfalon/openapi/types';
import { stringifyValue } from '@gonfalon/types/stringifyValue';
import type { SelectOption } from '@gonfalon/ui/select-with-search';
import { SelectWithSearch } from '@gonfalon/ui/select-with-search';
import clsx from 'clsx';

import { VariationDisplay } from '../../../ui/VariationDisplay';
import { getVariationFillColor } from '../../../utils/getVariationFillColor';

import type { VariationPickerProps } from './types';

import styles from './VariationPicker.module.css';

const ALL_VARIATIONS_KEY = '__all__';

const ALL_VARIATIONS_VARIATION: Variation = {
  _id: ALL_VARIATIONS_KEY,
  name: 'All variations',
  value: 'all',
  description: 'All variations',
};

const DEFAULT_PLACEHOLDER = 'Select variation';
const DEFAULT_SEARCH_PLACEHOLDER = 'Search variations';

// oxlint-disable-next-line max-lines-per-function
export function VariationPicker({
  selectedVariationIndex,
  variations,
  onSelectionChange,
  includeAllVariations = false,
  loading = false,
  selectProps,
  isDisabled = false,
  ...buttonProps
}: VariationPickerProps) {
  const selectedKey =
    selectedVariationIndex === -1
      ? ALL_VARIATIONS_KEY
      : selectedVariationIndex !== undefined &&
          selectedVariationIndex >= 0 &&
          selectedVariationIndex < variations.length
        ? (variations[selectedVariationIndex]?._id ?? stringifyValue(variations[selectedVariationIndex]?.value))
        : undefined;

  const allVariationsOption: SelectOption = {
    key: ALL_VARIATIONS_KEY,
    label: 'All variations',
  };

  const options: Array<SelectOption & { color?: string }> = [
    ...(includeAllVariations && variations.length > 1 ? [allVariationsOption] : []),
    ...variations.map((variation, index) => ({
      key: variation._id ?? stringifyValue(variation.value),
      label: variation.name ?? stringifyValue(variation.value),
      color: variation.color ?? getVariationFillColor(index),
    })),
  ];

  const getAriaLabel = () => {
    if (selectedVariationIndex === -1 && includeAllVariations) {
      return 'All variations';
    }
    if (selectedVariationIndex !== undefined && selectedVariationIndex >= 0 && variations[selectedVariationIndex]) {
      return variations[selectedVariationIndex].name ?? stringifyValue(variations[selectedVariationIndex].value);
    }
    return selectProps?.ariaLabel || selectProps?.placeholder || DEFAULT_PLACEHOLDER;
  };

  return (
    <SelectWithSearch
      loading={loading}
      disabled={isDisabled}
      options={options}
      selectedKey={selectedKey}
      onSelectionChange={(key) => {
        if (key === ALL_VARIATIONS_KEY) {
          onSelectionChange(ALL_VARIATIONS_VARIATION, -1);
          return;
        }
        const variationIndex = variations.findIndex((v) => v._id === key || stringifyValue(v.value) === key);
        if (variationIndex !== -1) {
          onSelectionChange(variations[variationIndex], variationIndex);
        }
      }}
      placeholder={DEFAULT_PLACEHOLDER}
      searchPlaceholder={DEFAULT_SEARCH_PLACEHOLDER}
      ariaLabel={getAriaLabel()}
      renderOption={(item) => {
        if (item.key === ALL_VARIATIONS_KEY) {
          return <VariationDisplay variation={ALL_VARIATIONS_VARIATION} variationIndex={0} multiple />;
        }
        const variationIndex = variations.findIndex((v) => v._id === item.key || stringifyValue(v.value) === item.key);
        return <VariationDisplay variation={variations[variationIndex]} variationIndex={variationIndex} />;
      }}
      renderSelectValue={(item) => {
        if (item.key === ALL_VARIATIONS_KEY) {
          return <VariationDisplay variation={ALL_VARIATIONS_VARIATION} variationIndex={-1} multiple />;
        }
        const variationIndex = variations.findIndex((v) => v._id === item.key || stringifyValue(v.value) === item.key);
        if (variationIndex === -1) {
          return null;
        }

        return <VariationDisplay variation={variations[variationIndex]} variationIndex={variationIndex} />;
      }}
      {...selectProps}
      buttonProps={{ ...buttonProps, className: clsx(styles.button, buttonProps?.className) }}
    />
  );
}
